import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, Calendar, DollarSign, TrendingUp, TrendingDown, 
  CheckCircle, AlertTriangle, Download, ArrowLeft, FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { monthlyClosuresAPI } from '../services/api';

interface MonthlyClosure {
  id: string;
  month: number;
  year: number;
  monthName: string;
  closedAt: string;
  closedBy: string;
  totalIncome: number;
  totalExpenses: number;
  openingBalance: number;
  closingBalance: number;
  status: 'open' | 'closed' | 'pending';
  transactionCount: number;
}

// Mock data removed — page fetches real data from monthlyClosuresAPI

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function MonthlyClosure() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [closures, setClosures] = useState<MonthlyClosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    loadClosures();
  }, []);

  const loadClosures = async () => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const response = await monthlyClosuresAPI.getAll(condoId);
      const data = response.data || response || [];
      setClosures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading closures:', err);
      setClosures([]);
    } finally {
      setLoading(false);
    }
  };

  const currentMonthData = closures.find(c => c.month === selectedMonth && c.year === selectedYear);
  const { isSuperAdmin, isAdmin } = useAuth();

  const handleCloseMonth = async () => {
    setIsClosing(true);
    try {
      const response = await monthlyClosuresAPI.create(user?.condoId || '', {
        period_year: selectedYear,
        period_month: selectedMonth + 1
      });
      
      const payload = response.data?.data ?? response.data ?? response;
      const rawClosure = payload?.closure || payload;
      const newClosure = { ...rawClosure, month: (rawClosure.period_month || selectedMonth + 1) - 1, year: rawClosure.period_year || selectedYear, monthName: monthNames[(rawClosure.period_month || selectedMonth + 1) - 1], status: 'closed' as const };
      setClosures(prev => {
        const filtered = prev.filter(c => !(c.month === selectedMonth && c.year === selectedYear));
        return [...filtered, newClosure];
      });
      
      setIsClosing(false);
      setShowCloseModal(false);
      alert(`✅ ${monthNames[selectedMonth]} ${selectedYear} cerrado exitosamente. Los movimientos ya no pueden ser editados.`);
    } catch (err) {
      console.error('Error closing month:', err);
      setIsClosing(false);
      alert('Error al cerrar el mes');
    }
  };

  const handleReopenMonth = async (closure: MonthlyClosure) => {
    if (!isSuperAdmin()) {
      alert('❌ Solo el Super Administrador puede reabrir meses cerrados.');
      return;
    }
    
    if (confirm(`¿Estás seguro de reabrir ${closure.monthName} ${closure.year}? Esto permitirá editar los movimientos de ese mes.`)) {
      alert('La reapertura de periodos no está disponible en el backend.');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'closed':
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-bold rounded-full"><Lock className="w-3 h-3" /> Cerrado</span>;
      case 'open':
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-bold rounded-full"><CheckCircle className="w-3 h-3" /> Abierto</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-bold rounded-full"><AlertTriangle className="w-3 h-3" /> Pendiente</span>;
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white dark:bg-[var(--v-surface-1)] border-b border-gray-200 dark:border-[var(--v-surface-3)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] dark:text-white hover:bg-gray-100 dark:hover:bg-[var(--v-surface-1)] dark:bg-[var(--v-surface-1)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#2D3748] dark:text-[var(--v-text-primary)]">Cierre Mensual</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400">Bloqueo de periodos contables</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        {/* Warning Banner */}
        <div className="bg-[#36CCD8]/10 border border-[#36CCD8]/30 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#36CCD8] rounded-xl flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#2D3748] dark:text-[var(--v-text-primary)] mb-1">¿Qué es el Cierre Mensual?</h2>
              <p className="text-gray-600 dark:text-slate-300">
                Al cerrar un mes, todos los ingresos y egresos quedan bloqueados para edición. 
                Esto garantiza la integridad de los reportes financieros. 
                <strong> Solo el Super Admin puede reabrir meses cerrados.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Current Month Status */}
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-[#2D3748] dark:text-white">Estado del Periodo</h3>
              <p className="text-gray-500 dark:text-slate-400">Selecciona el mes a cerrar o consultar</p>
            </div>
            <div className="flex gap-3">
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-4 py-2 border border-gray-200 dark:border-[var(--v-surface-3)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx} value={idx}>{name}</option>
                ))}
              </select>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 border border-gray-200 dark:border-[var(--v-surface-3)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
            </div>
          </div>

          {currentMonthData ? (
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-slate-950 rounded-xl p-4">
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">Estado</p>
                {getStatusBadge(currentMonthData.status)}
              </div>
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">Ingresos</p>
                <p className="text-xl font-bold text-emerald-600">${currentMonthData.totalIncome.toLocaleString()}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">Egresos</p>
                <p className="text-xl font-bold text-red-600">${currentMonthData.totalExpenses.toLocaleString()}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">Balance Final</p>
                <p className="text-xl font-bold text-blue-600">${currentMonthData.closingBalance.toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              No hay datos para {monthNames[selectedMonth]} {selectedYear}
            </div>
          )}

          {isAdmin() && currentMonthData?.status === 'open' && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <button 
                onClick={() => setShowCloseModal(true)}
                className="w-full md:w-auto px-6 py-3 bg-[#36CCD8] hover:bg-[#2DB8C4] text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <Lock className="w-5 h-5" /> Cerrar {monthNames[selectedMonth]} {selectedYear}
              </button>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
                ⚠️ Una vez cerrado, no podrás editar los movimientos de este mes.
              </p>
            </div>
          )}
        </div>

        {/* History */}
        <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-lg font-bold text-[#2D3748] dark:text-white">Historial de Cierres</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {closures
              .filter(c => c.status === 'closed')
              .sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime())
              .map(closure => (
              <div key={closure.id} className="p-6 hover:bg-gray-50 dark:bg-slate-950 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-bold text-[#2D3748] dark:text-white">{closure.monthName} {closure.year}</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Cerrado el {new Date(closure.closedAt).toLocaleDateString('es-MX')} por {closure.closedBy}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-slate-500">{closure.transactionCount} transacciones</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-slate-400">Balance Final</p>
                      <p className="text-xl font-bold text-[#36CCD8]">${closure.closingBalance.toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-4 py-2 bg-[#36CCD8]/10 text-[#36CCD8] font-bold rounded-xl hover:bg-[#36CCD8]/20 flex items-center gap-2">
                        <Download className="w-4 h-4" /> Reporte
                      </button>
                      {isSuperAdmin() && (
                        <button 
                          onClick={() => handleReopenMonth(closure)}
                          className="px-4 py-2 bg-amber-100 text-amber-700 font-bold rounded-xl hover:bg-amber-200"
                        >
                          Reabrir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {closures.filter(c => c.status === 'closed').length === 0 && (
              <div className="p-8 text-center text-gray-500 dark:text-slate-400">
                No hay meses cerrados aún.
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Close Confirmation Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[var(--v-surface-1)] rounded-2xl max-w-md w-full p-6">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-[#2D3748] dark:text-white text-center mb-2">
              ¿Cerrar {monthNames[selectedMonth]} {selectedYear}?
            </h3>
            <p className="text-gray-600 dark:text-slate-300 text-center mb-6">
              Esta acción bloqueará la edición de todos los ingresos y egresos de este mes. 
              Los reportes financieros quedarán fijos.
            </p>
            <div className="bg-gray-50 dark:bg-slate-950 rounded-xl p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 dark:text-slate-300">Transacciones:</span>
                <span className="font-bold">{currentMonthData?.transactionCount || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-slate-300">Balance Final:</span>
                <span className="font-bold text-[#36CCD8]">${currentMonthData?.closingBalance.toLocaleString() || 0}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowCloseModal(false)}
                className="flex-1 py-3 border border-gray-200 dark:border-[var(--v-surface-3)] text-gray-700 dark:text-slate-200 font-bold rounded-xl hover:bg-gray-50 dark:bg-slate-950"
              >
                Cancelar
              </button>
              <button 
                onClick={handleCloseMonth}
                disabled={isClosing}
                className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] disabled:opacity-50"
              >
                {isClosing ? 'Cerrando...' : 'Sí, Cerrar Mes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
